import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Loader2, Facebook, Instagram } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    objectif: "remise_en_forme", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé !");
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <section className="bg-fegray-light pt-16 pb-12 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-4">
            — Demande de renseignements
          </p>
          <h1 className="text-4xl lg:text-5xl font-bold text-fegray-dark">
            Parlons de <span className="text-fegreen">vos objectifs</span>
          </h1>
          <p className="mt-5 text-fegray">Réponse envoyée sous 24h.</p>
        </div>
      </section>

      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-7">
            <ContactItem icon={Phone} label="Téléphone" value="07 66 25 31 43" />
            <ContactItem icon={Mail} label="Email" value="rosariveti@gmail.com" />
            <ContactItem icon={MapPin} label="Zones" value="Istres · Fos-sur-Mer · Miramas · Saint-Mitre · Port-de-Bouc" />

            <div className="fe-card p-6 mt-8">
              <p className="text-xs uppercase tracking-wider text-fegreen font-bold mb-3">Suivez-nous</p>
              <div className="flex gap-3">
                <a href="#" aria-label="Facebook" className="w-10 h-10 rounded-full bg-fegreen-light flex items-center justify-center hover:bg-fegreen hover:text-white transition text-fegreen">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="#" aria-label="Instagram" className="w-10 h-10 rounded-full bg-fegreen-light flex items-center justify-center hover:bg-fegreen hover:text-white transition text-fegreen">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div className="fe-card p-6">
              <p className="text-xs uppercase tracking-wider text-fegray mb-2">Note moyenne</p>
              <div className="text-4xl font-extrabold text-fegreen">4.9 / 5</div>
              <p className="text-sm text-fegray mt-2">Sur 34 avis Google</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div className="fe-card p-10 text-center" data-testid="contact-success">
                <div className="w-16 h-16 rounded-full bg-fegreen-light flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8 text-fegreen" />
                </div>
                <h3 className="text-2xl font-bold text-fegray-dark mb-3">Message reçu</h3>
                <p className="text-fegray">Rosalie vous répondra sous 24h.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="fe-card p-7 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="Prénom *" name="prenom" value={form.prenom} onChange={set} required />
                  <Input label="Nom *" name="nom" value={form.nom} onChange={set} required />
                  <Input label="Email *" type="email" name="email" value={form.email} onChange={set} required />
                  <Input label="Téléphone *" name="telephone" value={form.telephone} onChange={set} required />
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-fegray mb-2">Quel est votre objectif ?</label>
                  <select
                    value={form.objectif}
                    onChange={(e) => set("objectif", e.target.value)}
                    data-testid="contact-objectif"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
                  >
                    <option value="reprise_activite">Reprise d&apos;activité physique</option>
                    <option value="remise_en_forme">Remise en forme</option>
                    <option value="sante_bien_etre">Santé &amp; Bien-être</option>
                    <option value="sport_performance">Sport &amp; Performance</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs uppercase tracking-wider text-fegray mb-2">Précisions sur votre objectif</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    data-testid="contact-message"
                    placeholder="Décrivez vos attentes, contraintes, disponibilités..."
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="btn-green w-full disabled:opacity-50"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Envoi…</> : <>Envoyer ma demande <Send className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ContactItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-5">
      <div className="w-12 h-12 rounded-full bg-fegreen-light flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-fegreen" strokeWidth={1.8} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-fegray">{label}</div>
        <div className="text-lg text-fegray-dark mt-1 font-medium">{value}</div>
      </div>
    </div>
  );
}

function Input({ label, type = "text", name, value, onChange, required }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-fegray mb-2">{label}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(name, e.target.value)}
        data-testid={`contact-${name}`}
        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-fegray-dark focus:border-fegreen focus:ring-2 focus:ring-fegreen/20 focus:outline-none"
      />
    </div>
  );
}
